import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, ResponsiveContainer, LabelList } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import MIHeader from '../MIHeader';
import { fmtNum, fmtMonth, fmtMonthShort, slugify, detectSeasonalPattern, MONTH_NAMES, getFYStart, getFYPlainLabel, fetchAllPaginated, fetchFortnightFYByMill, fetchLatestFortnightBreakdown, fetchGapFillingFortnightsByMill, buildMergedPeriods, formatFortnightRange, RANK_DISCLOSURE } from '../utils';
import { generateFactoryPDF } from '../shareExport';
import NearbyMillsDistanceList from '../NearbyMillsDistanceList';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../../hooks/useTranslation';
import { localizedFactoryName, localizedStateName, localizedDistrictName } from '../../../utils/localizedDisplayName';
import { fetchFactoryTenders } from '../../live-tender-info/factoryTenders';
import { fetchMandiOfferForMill } from '../../mandi/data';
import { normalizeGrade, buildBuyNowUrl } from '../../live-tender-info/matching';

const BRAND_BLUE = '#1e4a72';
const BRAND_BLUE_TINT = '#EFF6FF';

const fmtTenderDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtRate = (n) => (n != null ? `₹${n.toLocaleString('en-IN')}/MT` : '—');

const ShareIcon = ({ size = 16, color = '#0F2238' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
  </svg>
);

const FactoryPage = () => {
  const { millSlug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mill, setMill] = useState(null);
  const [history, setHistory] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [ranks, setRanks] = useState(null);
  const [fyCumulative, setFyCumulative] = useState(0);
  const [fyMonth, setFyMonth] = useState(null);
  const [fortnight, setFortnight] = useState(null);
  const [fortnightMt, setFortnightMt] = useState(null);
  const [gapFortnights, setGapFortnights] = useState([]);
  const [seasonal, setSeasonal] = useState(null);
  const [chartType, setChartType] = useState('line');
  const [sharing, setSharing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [toast, setToast] = useState('');
  const [liveTenders, setLiveTenders] = useState([]);
  const [mandiOffer, setMandiOffer] = useState(null);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [compareState, setCompareState] = useState('');
  const [compareDistrict, setCompareDistrict] = useState('');
  const [compareMill, setCompareMill] = useState('');
  const [compareSearch, setCompareSearch] = useState('');
  const [allMillsForPicker, setAllMillsForPicker] = useState([]);
  const [news, setNews] = useState([]);
  const [buyerPincode, setBuyerPincode] = useState('');
  const [distanceKm, setDistanceKm] = useState(null);
  const [distLoading, setDistLoading] = useState(false);
  const [distError, setDistError] = useState('');
  const shareCardRef = useRef(null);
  const historyChartRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: allMills, error: mErr } = await supabase
          .from('mi_mills')
          .select('id, mill_code, canonical_name, source_state, source_district, canonical_district_id, pincode');
        if (mErr) throw mErr;
        const match = (allMills || []).find((m) => slugify(m.canonical_name) === millSlug);
        if (!match) throw new Error('Factory not found');

        // Wave 1: these 7 only depend on match.id/match.canonical_name, not on each
        // other - fire together instead of one after another (was 5 sequential round
        // trips, now 1; the tender + Buy Now lookups ride along the same wave since
        // they have the same dependency shape).
        const [
          { data: hist, error: hErr },
          { data: metricsRow },
          { data: rankRow },
          { count: totalMills },
          { data: newsRows },
          { tenders: tenderRows, error: tendersErr },
          { offer: offerData, error: offerErr },
        ] = await Promise.all([
          supabase.from('mi_release_orders').select('period_month, allocated_qty_mt').eq('mill_id', match.id).order('period_month', { ascending: true }),
          supabase.from('mi_v_mill_metrics').select('*').eq('mill_id', match.id).maybeSingle(),
          supabase.from('mi_v_mill_rank_latest').select('national_rank, state_rank, district_rank, canonical_state, canonical_district, period_month').eq('mill_id', match.id).maybeSingle(),
          supabase.from('mi_mills').select('id', { count: 'exact', head: true }),
          supabase.from('mi_factory_news').select('title, url, published_at, language').eq('mill_id', match.id).order('published_at', { ascending: false, nullsFirst: false }).limit(3),
          // Live Tender Info is temporarily disabled pending legal/compliance review
          // of the scraped source data (see src/pages/live-tender-info/ and
          // src/Routes.jsx for the other disable points). Stubbed to an empty
          // result here — same shape fetchFactoryTenders() would return — instead
          // of calling it, so this factory page never fetches or renders any
          // scraped tender rows (the section below and the PDF export both key off
          // this being empty). To restore: swap this line back to
          // `fetchFactoryTenders(match.canonical_name)`.
          Promise.resolve({ tenders: [], error: null }),
          fetchMandiOfferForMill(match.id),
        ]);
        if (hErr) throw hErr;
        // Live Tender Info / Buy Now are supplementary - a failure there shouldn't
        // block the core factory report from rendering, so these are logged only.
        if (tendersErr) console.error('fetchFactoryTenders:', tendersErr);
        if (offerErr) console.error('fetchMandiOfferForMill:', offerErr);

        const latestMonth = metricsRow?.latest_month;
        const fyStart = latestMonth ? getFYStart(latestMonth) : null;

        // Wave 2: state/district totals depend on rankRow, FY total depends on
        // metricsRow - neither depends on the other, so run together too
        // (was 2 more sequential round trips, now 1 - plus the FY pagination
        // that was already happening after them).
        const [stateResult, districtResult, fyRows, fortnightFY, fortnightBreakdown, gapFortnightsData] = await Promise.all([
          rankRow
            ? supabase.from('mi_v_state_monthly').select('mills_reporting').eq('canonical_state', rankRow.canonical_state).eq('period_month', rankRow.period_month).maybeSingle()
            : Promise.resolve({ data: null }),
          rankRow
            ? supabase.from('mi_v_district_monthly').select('mills_reporting').eq('canonical_district', rankRow.canonical_district).eq('period_month', rankRow.period_month).maybeSingle()
            : Promise.resolve({ data: null }),
          latestMonth
            ? fetchAllPaginated(() => supabase.from('mi_release_orders').select('allocated_qty_mt').eq('mill_id', match.id).gte('period_month', fyStart).lte('period_month', latestMonth))
            : Promise.resolve([]),
          fetchFortnightFYByMill(latestMonth),
          fetchLatestFortnightBreakdown(),
          fetchGapFillingFortnightsByMill(latestMonth),
        ]);
        const stateTotalMills = stateResult?.data?.mills_reporting ?? null;
        const districtTotalMills = districtResult?.data?.mills_reporting ?? null;
        // Top up with this mill's share of any fortnightly release not yet folded
        // into a combined mi_release_orders row (see fetchFortnightFYByMill).
        const fyTotal = fyRows.reduce((s, r) => s + Number(r.allocated_qty_mt), 0) + (fortnightFY.get(match.id) || 0);

        if (!isMounted) return;
        setMill(match);
        setHistory(hist || []);
        setMetrics(metricsRow || null);
        setRanks({ ...(rankRow || {}), totalMills: totalMills || 0, stateTotalMills, districtTotalMills });
        setFyCumulative(fyTotal);
        setFyMonth(latestMonth || null);
        setFortnight(fortnightBreakdown);
        setFortnightMt(fortnightBreakdown ? (fortnightBreakdown.byMill.get(match.id) ?? 0) : null);
        setGapFortnights((gapFortnightsData || []).filter((f) => f.millId === match.id));
        setSeasonal(detectSeasonalPattern(hist || []));
        setAllMillsForPicker(allMills || []);
        setNews(newsRows || []);
        setLiveTenders(tenderRows || []);
        setMandiOffer(offerData || null);
      } catch (e) {
        if (isMounted) setError(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [millSlug]);

  // Last-12-mo average tile keeps using history-only last12 (real, full-month
  // figures) - never diluted by a partial-fortnight qty, which would understate it.
  const last12 = history.slice(-12).reverse();
  const chartData = history.map((r) => ({ month: fmtMonthShort(r.period_month), mt: Number(r.allocated_qty_mt) }));

  // Merged, date-descending list of {monthly rows} ∪ {this mill's gap-filling
  // fortnights} - used by the "previous period" tile and the month-by-month table
  // below, so both reflect the September fortnight gap without either mislabeling a
  // half-month figure as a full month's.
  const mergedPeriods = buildMergedPeriods(history, gapFortnights);
  // Index 0 is claimed by the top "current" tile above whenever it's showing (it's
  // always the same record - the active fortnight is itself always the newest
  // gap-filling entry); otherwise nothing above has claimed it, so this tile becomes
  // the most recent record itself.
  const previousPeriod = mergedPeriods[fortnight ? 1 : 0] || null;

  const compareStates = [...new Set(allMillsForPicker.map((m) => m.source_state))].filter(Boolean).sort();
  const compareStateCanonical = compareStates.find((s) => slugify(s) === compareState);
  const compareDistrictsForState = compareState
    ? [...new Set(allMillsForPicker.filter((m) => slugify(m.source_state) === compareState).map((m) => m.source_district))].filter(Boolean).sort()
    : [];
  const compareMillsForState = compareState
    ? allMillsForPicker.filter((m) => slugify(m.source_state) === compareState && (!compareDistrict || slugify(m.source_district) === compareDistrict))
    : [];
  const compareSearchResults = compareSearch.trim().length >= 2
    ? allMillsForPicker.filter((m) => m.canonical_name.toLowerCase().includes(compareSearch.toLowerCase())).slice(0, 8)
    : [];

  const fireToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 1800); };

  // Grade-level Buy Now match: this factory's own live tenders vs SugarHathi's
  // own live/in-stock mill_price_quotes for this SAME mill_id (mandiOffer) -
  // never a name-based guess. A tender only gets a Buy Now button when its grade
  // (mechanically normalized) has a matching, currently-available SugarHathi
  // quote; tender data alone is never sufficient.
  const tendersWithMatch = liveTenders.map((tender) => {
    const quote = mandiOffer ? mandiOffer.grades.get(normalizeGrade(tender.grade)) : null;
    const matched = !!(quote && quote.availableQty > 0);
    return { ...tender, matched, mandiQuoteId: matched ? quote.id : null, mandiGradeCode: matched ? quote.grade : null };
  });

  const handleSharePDF = async () => {
    setSharing(true);
    // Computed here (not at fetch time) so it always reflects whatever
    // language is active right now, even if the user switched languages
    // after the page's data finished loading.
    const fyLabel = fyMonth ? getFYPlainLabel(fyMonth, t) : '';
    await generateFactoryPDF({ mill, metrics, ranks, fyCumulative, fyLabel, fortnight, fortnightMt, history, news, chartRef: historyChartRef, action: 'share', language, t, liveTenders: tendersWithMatch, mandiOffer });
    setSharing(false);
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    const fyLabel = fyMonth ? getFYPlainLabel(fyMonth, t) : '';
    try {
      await generateFactoryPDF({ mill, metrics, ranks, fyCumulative, fyLabel, fortnight, fortnightMt, history, news, chartRef: historyChartRef, action: 'download', language, t, liveTenders: tendersWithMatch, mandiOffer });
    } catch (e) {
      fireToast(t('mandiFactory.pdfGenerationFailed', 'Could not generate PDF — please try again.'));
    }
    setDownloadingPdf(false);
  };

  // Shares the factory PAGE URL itself (not a PDF file) so the recipient lands
  // on this exact factory's page - native share sheet where available, clipboard
  // copy otherwise.
  const handleShareLink = async () => {
    const url = window.location.href;
    const title = mill ? localizedFactoryName(mill, language) : 'SugarHathi';
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch (e) { /* cancelled/unsupported - fall through */ }
    }
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      fireToast(t('mandi.linkCopied', 'Link copied'));
    }
  };

  const handleGoCompare = () => {
    if (compareMill) navigate(`/market-intelligence/compare?a=${slugify(mill.canonical_name)}&b=${slugify(compareMill)}`);
  };

  const handleBuyerPincodeChange = (value) => {
    const pin = value?.replace(/\D/g, '').substring(0, 6);
    setBuyerPincode(pin);
    setDistanceKm(null);
    setDistError('');
    if (pin?.length === 6 && mill?.pincode?.length === 6) {
      fetchFactoryDistance(pin);
    }
  };

  const fetchFactoryDistance = async (destPin) => {
    setDistLoading(true);
    setDistError('');
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${supabaseUrl}/functions/v1/calculate-route-distance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originPincode: mill?.pincode, destinationPincode: destPin }),
      });
      if (resp?.ok) {
        const data = await resp.json();
        setDistanceKm(data?.distanceKm ?? null);
        if (data?.distanceKm == null) setDistError('Could not calculate distance for this pincode.');
      } else {
        setDistError('Could not calculate distance for this pincode.');
      }
    } catch {
      setDistError('Could not calculate distance right now. Please try again.');
    } finally {
      setDistLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0F2238' }}>
      <Helmet>
        <title>{mill ? `${mill.canonical_name} — Sugar Mill Quota Data | SugarHathi` : 'Sugar Mill Quota Data | SugarHathi Market Intelligence'}</title>
        <meta name="description" content={mill ? `Live sugar quota release-order data for ${mill.canonical_name}, ${mill.source_district}, ${mill.source_state}. Monthly allocation history, verified by SugarHathi.` : 'Live mill-wise sugar quota release-order data across India, verified by SugarHathi.'} />
      </Helmet>
      <MIHeader subtitle={mill ? localizedFactoryName(mill, language) : t('common.factory', 'Factory')} />
      <main className="min-h-[calc(100vh-64px)]" style={{ background: '#FDF8F0' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl py-8">
          {loading && (
            <div className="flex flex-col items-center justify-center py-24" style={{ color: '#78716C' }}>
              <Icon name="Loader" size={32} className="animate-spin mb-3" /><p>{t('common.loading', 'Loading...')}</p>
            </div>
          )}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-24" style={{ color: '#78716C' }}>
              <Icon name="AlertTriangle" size={32} className="mb-3" style={{ color: '#DC2626' }} /><p>{error}</p>
            </div>
          )}
          {!loading && !error && mill && metrics && (
            <>
              <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-black" style={{ color: '#0F2238' }}>{localizedFactoryName(mill, language)}</h1>
                <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                  <button onClick={handleShareLink}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-[10px]"
                    style={{ background: 'transparent', border: `2px solid ${BRAND_BLUE}`, color: BRAND_BLUE }}>
                    <Icon name="Share2" size={14} color={BRAND_BLUE} /> {t('mandiFactory.share', 'Share')}
                  </button>
                  <button onClick={handleDownloadPdf} disabled={downloadingPdf}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-[10px] text-white disabled:opacity-50"
                    style={{ background: '#ff8c00' }}>
                    <Icon name="Download" size={14} color="#fff" /> {downloadingPdf ? t('mandiFactory.generatingPdf', 'Generating…') : t('mandiFactory.downloadPdf', 'Download PDF')}
                  </button>
                  <button onClick={handleSharePDF} disabled={sharing}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-[10px] disabled:opacity-50"
                    style={{ background: 'transparent', border: '2px solid #F59E0B', color: '#B45309' }}>
                    <ShareIcon size={14} color="#B45309" /> {sharing ? 'Preparing PDF...' : 'Share as PDF'}
                  </button>
                </div>
              </div>
              <p className="text-sm mb-6" style={{ color: '#78716C' }}>
                {mill.source_district && (
                  <>
                    <button onClick={() => navigate(`/market-intelligence/district/${slugify(mill.source_district)}`)} className="hover:underline font-medium" style={{ color: '#B45309' }}>{localizedDistrictName(mill.source_district, mill.source_state, language)}</button>
                    {', '}
                  </>
                )}
                {localizedStateName(mill.source_state, language)} &middot; Code {mill.mill_code}
              </p>

              {/* KPI blocks - pastel tints matching the reference */}
              <div className="grid grid-cols-2 gap-3 mb-6" ref={shareCardRef}>
                {fortnight && (
                  <div className="col-span-2 rounded-[14px] p-4" style={{ background: '#FEF3F2', border: '1px solid #FBD5D5' }}>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#B42318' }}>
                      {t('marketIntelligence.fortnightColumnLabel', { range: formatFortnightRange(fortnight.periodStart, fortnight.periodEnd, language) || fortnight.periodLabel, defaultValue: '{{range}} (MT)' })}
                    </p>
                    <p className="text-lg font-black" style={{ color: '#0F2238' }}>{fortnightMt !== null ? fmtNum(fortnightMt) : 'N/A'} MT</p>
                  </div>
                )}
                {previousPeriod && (
                  <div className="rounded-[14px] p-4" style={{ background: '#FFF3E0', border: '1px solid #FDE1B8' }}>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#B45309' }}>
                      {previousPeriod.kind === 'fortnight'
                        ? t('marketIntelligence.previousPeriodNote', 'Previous period')
                        : t('marketIntelligence.completeMonthNote', 'Last complete month')}
                      {' '}({previousPeriod.kind === 'fortnight'
                        ? (formatFortnightRange(previousPeriod.periodStart, previousPeriod.periodEnd, language) || previousPeriod.periodLabel)
                        : fmtMonthShort(previousPeriod.periodMonth)})
                    </p>
                    <p className="text-lg font-black" style={{ color: '#0F2238' }}>{fmtNum(previousPeriod.qty)} MT</p>
                  </div>
                )}
                <div className="rounded-[14px] p-4" style={{ background: '#E7F7EF', border: '1px solid #B7E8CE' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#15803D' }}>Peak ({fmtMonth(metrics.max_qty_month)})</p>
                  <p className="text-lg font-black" style={{ color: '#15803D' }}>{fmtNum(metrics.max_qty_mt)} MT</p>
                </div>
                <div className="rounded-[14px] p-4" style={{ background: '#EEF0FE', border: '1px solid #D2D6FB' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#4338CA' }}>Last 12-mo average</p>
                  <p className="text-lg font-black" style={{ color: '#0F2238' }}>
                    {fmtNum(Math.round(last12.reduce((s, r) => s + Number(r.allocated_qty_mt), 0) / (last12.length || 1)))} MT
                  </p>
                </div>
                <div className="rounded-[14px] p-4" style={{ background: metrics.cagr_pct >= 0 ? '#E7F7EF' : '#FDECEC', border: metrics.cagr_pct >= 0 ? '1px solid #B7E8CE' : '1px solid #F8C9C9' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: metrics.cagr_pct >= 0 ? '#15803D' : '#B91C1C' }}>Growth (CAGR)</p>
                  <p className="text-lg font-black" style={{ color: metrics.cagr_pct === null ? '#78716C' : metrics.cagr_pct >= 0 ? '#15803D' : '#B91C1C' }}>
                    {metrics.cagr_pct !== null ? `${metrics.cagr_pct >= 0 ? '+' : ''}${metrics.cagr_pct}%` : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Live Tender Info for THIS factory only — visually separated from the
                  general quota/allocation data above via its own bordered card and
                  brand-blue accent (never green). Buy Now only ever appears per-grade,
                  and only when mandiOffer (SugarHathi's own live mill_price_quotes for
                  this exact mill_id) actually has that grade in stock. */}
              {tendersWithMatch.length > 0 && (
                <section className="mb-6 rounded-[16px] overflow-hidden" style={{ border: `1px solid ${BRAND_BLUE}30`, background: '#FFFFFF' }}>
                  <div className="flex items-center gap-2 px-4 py-3" style={{ background: BRAND_BLUE }}>
                    <Icon name="FileText" size={15} color="#FFFFFF" />
                    <span className="text-sm font-bold text-white">{t('liveTenderInfo.title', 'Live Tender Info')}</span>
                  </div>
                  <div className="p-4 space-y-3">
                    {tendersWithMatch.map((tender) => (
                      <div key={tender.id} className="rounded-[12px] p-3.5" style={{ background: BRAND_BLUE_TINT, border: '1px solid #BFDBFE' }}>
                        <div className="flex items-center justify-between gap-3 mb-2.5 flex-wrap">
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-black" style={{ background: '#FEF3C7', color: '#B45309' }}>
                            {t('marketplace.grade', 'Grade')} {tender.grade}
                          </span>
                          <span className="text-sm font-black" style={{ color: '#0F2238' }}>{fmtRate(tender.rateExclGst)}</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs" style={{ color: '#44403C' }}>
                          <div>{t('liveTenderInfo.tenderDateTime', 'Updated Date')}<br /><span className="font-semibold" style={{ color: '#0F2238' }}>{fmtTenderDate(tender.tenderDateTime)}</span></div>
                          {tender.season && <div>{t('marketplace.season', 'Season')}<br /><span className="font-semibold" style={{ color: '#0F2238' }}>{tender.season}</span></div>}
                          {tender.paymentDate && <div>{t('marketplace.paymentDate', 'Payment Date')}<br /><span className="font-semibold" style={{ color: '#0F2238' }}>{fmtTenderDate(tender.paymentDate)}</span></div>}
                          {tender.liftingDate && <div>{t('marketplace.liftingDate', 'Lifting Date')}<br /><span className="font-semibold" style={{ color: '#0F2238' }}>{fmtTenderDate(tender.liftingDate)}</span></div>}
                        </div>
                        {tender.matched && (
                          <button
                            onClick={() => navigate(buildBuyNowUrl({ mandiFactorySlug: mandiOffer.factorySlug, mandiGradeCode: tender.mandiGradeCode, mandiQuoteId: tender.mandiQuoteId }))}
                            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-white text-[11px]"
                            style={{ background: '#ff8c00' }}
                          >
                            <Icon name="ShoppingBag" size={12} color="#fff" />
                            {t('liveTenderInfo.buyNow', 'Buy Now on Mandi')}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="px-4 pb-4">
                    <p className="text-[11px] leading-relaxed" style={{ color: '#78716C' }}>
                      {t('liveTenderInfo.disclaimer', "Data and information displayed on this platform may be sourced from third-party sources and are provided for general informational purposes only. Such information is not owned, controlled or guaranteed by SugarHathi. SugarHathi does not represent third-party data as its own offering and does not monetise the underlying third-party information.")}
                    </p>
                  </div>
                </section>
              )}

              {/* Month by month table */}
              <p className="text-sm font-bold mb-2" style={{ color: '#0F2238' }}>Month by month, last 12 months</p>
              <div className="rounded-[10px] overflow-hidden mb-6" style={{ border: '1px solid #E7E0D3' }}>
                <div className="grid grid-cols-2 px-3 py-2" style={{ background: '#F5EDDD' }}>
                  <span className="text-xs font-semibold" style={{ color: '#78716C' }}>Month</span>
                  <span className="text-xs font-semibold text-right" style={{ color: '#78716C' }}>Allocation</span>
                </div>
                {mergedPeriods.slice(0, 12).map((r, i) => (
                  <div key={i} className="grid grid-cols-2 px-3 py-2.5" style={{ background: i % 2 === 0 ? '#FFFFFF' : '#FDF6E9' }}>
                    <span className="text-sm" style={{ color: '#44403C' }}>
                      {r.kind === 'fortnight' ? (formatFortnightRange(r.periodStart, r.periodEnd, language) || r.periodLabel) : fmtMonth(r.periodMonth)}
                    </span>
                    <span className="text-sm font-semibold text-right" style={{ color: '#0F2238' }}>{fmtNum(r.qty)} MT</span>
                  </div>
                ))}
              </div>

              {/* Distance calculator */}
              <div className="rounded-[14px] p-4 mb-6" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                <p className="text-sm font-bold mb-1" style={{ color: '#0F2238' }}>{mill?.pincode ? t('mandiFactory.distanceTitleWithPincode', { pincode: mill.pincode }) : t('mandiFactory.distanceTitleNoPincode')}</p>
                {mill?.pincode ? (
                  <>
                    <p className="text-xs mb-3" style={{ color: '#78716C' }}>{t('mandiFactory.enterPincodeHint')}</p>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={buyerPincode}
                        onChange={(e) => handleBuyerPincodeChange(e?.target?.value)}
                        placeholder={t('mandiFactory.pincodePlaceholder')}
                        className="flex-1 rounded-[10px] border px-3 py-2 text-sm font-semibold"
                        style={{
                          borderColor: buyerPincode && buyerPincode?.length !== 6 ? '#F8C9C9' : '#E7E0D3',
                          background: '#FFFFFF',
                          color: '#0F2238',
                        }}
                      />
                    </div>
                    {distLoading && (
                      <div className="flex items-center gap-2 py-1">
                        <div className="w-3 h-3 border-2 rounded-full animate-spin" style={{ borderColor: '#F59E0B', borderTopColor: 'transparent' }}></div>
                        <p className="text-xs" style={{ color: '#78716C' }}>{t('mandiFactory.calculatingDistance')}</p>
                      </div>
                    )}
                    {!distLoading && distError && (
                      <p className="text-xs" style={{ color: '#B91C1C' }}>{distError}</p>
                    )}
                    {!distLoading && !distError && distanceKm !== null && (
                      <div className="rounded-[10px] p-3 text-center" style={{ background: '#E7F7EF', border: '1px solid #B7E8CE' }}>
                        <p className="text-xs font-medium" style={{ color: '#78716C' }}>{t('mandiFactory.approxRoadDistance')}</p>
                        <p className="text-lg font-black" style={{ color: '#15803D' }}>{distanceKm} km</p>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs" style={{ color: '#A8A29E' }}>{t('mandiFactory.distanceNotAvailable')}</p>
                )}
              </div>

              {/* Rankings table */}
              <p className="text-sm font-bold mb-2" style={{ color: '#0F2238' }}>Rankings</p>
              <div className="rounded-[10px] overflow-hidden mb-1" style={{ border: '1px solid #E7E0D3' }}>
                <div className="grid grid-cols-[1fr_auto] px-3 py-2.5" style={{ background: '#FFFFFF' }}>
                  <span className="text-sm" style={{ color: '#44403C' }}>National Rank &mdash; {fmtMonthShort(metrics.latest_month)} Allocation</span>
                  <span className="text-sm font-bold" style={{ color: '#0F2238' }}>{ranks.national_rank} of {ranks.totalMills}</span>
                </div>
                <div className="grid grid-cols-[1fr_auto] px-3 py-2.5" style={{ background: '#FDF6E9' }}>
                  <span className="text-sm" style={{ color: '#44403C' }}>State Rank &mdash; {fmtMonthShort(metrics.latest_month)} Allocation ({localizedStateName(mill.source_state, language)})</span>
                  <span className="text-sm font-bold" style={{ color: '#0F2238' }}>{ranks.state_rank ?? 'N/A'}{ranks.stateTotalMills ? ` of ${ranks.stateTotalMills}` : ''}</span>
                </div>
                {ranks.district_rank && (
                  <div className="grid grid-cols-[1fr_auto] px-3 py-2.5" style={{ background: '#FFFFFF' }}>
                    <span className="text-sm" style={{ color: '#44403C' }}>District Rank &mdash; {fmtMonthShort(metrics.latest_month)} Allocation</span>
                    <span className="text-sm font-bold" style={{ color: '#0F2238' }}>{ranks.district_rank}{ranks.districtTotalMills ? ` of ${ranks.districtTotalMills}` : ''}</span>
                  </div>
                )}
              </div>
              <p className="text-xs mb-6" style={{ color: '#A8A29E' }}>{RANK_DISCLOSURE(fmtMonthShort(metrics.latest_month))}</p>

              {/* Full history chart */}
              <p className="text-sm font-bold mb-2" style={{ color: '#0F2238' }}>Full history, {fmtMonthShort(metrics.first_month)} &ndash; {fmtMonthShort(metrics.last_month)} ({metrics.months_reporting} months)</p>
              <div className="h-40 mb-6 rounded-[10px] p-2" style={{ background: '#FFFFFF', border: '1px solid #E7E0D3' }} ref={historyChartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis dataKey="month" stroke="#A8A29E" fontSize={9} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(chartData.length / 8))} />
                    <Line type="monotone" dataKey="mt" stroke="#F59E0B" strokeWidth={1.5} dot={{ r: 2, fill: '#F59E0B' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Seasonal pattern - with visible data labels */}
              {seasonal && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-bold" style={{ color: '#0F2238' }}>
                      Seasonal pattern: {MONTH_NAMES[seasonal.direction === 'AtoB' ? seasonal.monthA : seasonal.monthB]} vs {MONTH_NAMES[seasonal.direction === 'AtoB' ? seasonal.monthB : seasonal.monthA]}
                    </p>
                    <div className="flex gap-1 rounded-[10px] p-0.5" style={{ background: '#F5EDDD' }}>
                      <button onClick={() => setChartType('bar')} className="text-xs font-medium px-2.5 py-1 rounded-md" style={chartType === 'bar' ? { background: '#F59E0B', color: '#FFFFFF' } : { color: '#78716C' }}>Bar</button>
                      <button onClick={() => setChartType('line')} className="text-xs font-medium px-2.5 py-1 rounded-md" style={chartType === 'line' ? { background: '#F59E0B', color: '#FFFFFF' } : { color: '#78716C' }}>Line</button>
                    </div>
                  </div>
                  <p className="text-xs mb-3" style={{ color: '#A8A29E' }}>Same direction in {Math.round(seasonal.pct * 100)}% of {seasonal.years} years observed</p>
                  <div className="h-36 rounded-[10px] p-2" style={{ background: '#FFFFFF', border: '1px solid #E7E0D3' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {chartType === 'bar' ? (
                        <BarChart data={buildSeasonalSeries(history, seasonal)} margin={{ top: 20 }}>
                          <XAxis dataKey="year" stroke="#A8A29E" fontSize={9} tickLine={false} axisLine={false} />
                          <Bar dataKey="a" fill="#F59E0B" radius={[3, 3, 0, 0]}><LabelList dataKey="a" position="top" style={{ fontSize: 9, fill: '#78716C' }} /></Bar>
                          <Bar dataKey="b" fill="#3B82F6" radius={[3, 3, 0, 0]}><LabelList dataKey="b" position="top" style={{ fontSize: 9, fill: '#78716C' }} /></Bar>
                        </BarChart>
                      ) : (
                        <LineChart data={buildSeasonalSeries(history, seasonal)} margin={{ top: 20 }}>
                          <XAxis dataKey="year" stroke="#A8A29E" fontSize={9} tickLine={false} axisLine={false} />
                          <Line type="monotone" dataKey="a" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3, fill: '#F59E0B' }}><LabelList dataKey="a" position="top" style={{ fontSize: 9, fill: '#78716C' }} /></Line>
                          <Line type="monotone" dataKey="b" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3, fill: '#3B82F6' }}><LabelList dataKey="b" position="top" style={{ fontSize: 9, fill: '#78716C' }} /></Line>
                        </LineChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Latest news */}
              <p className="text-sm font-bold mb-2" style={{ color: '#0F2238' }}>{t('marketIntelligence.latestNews')}</p>
              <div className="rounded-[14px] p-4 mb-6" style={{ background: '#FFFFFF', border: '1px solid #E7E0D3' }}>
                {news.length === 0 ? (
                  <p className="text-sm" style={{ color: '#A8A29E' }}>{t('marketIntelligence.noLatestArticles')}</p>
                ) : (
                  <div className="space-y-3">
                    {news.map((n, i) => (
                      <a key={i} href={n.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 group">
                        <Icon name="ExternalLink" size={13} color="#B45309" className="flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <span className="text-sm truncate block group-hover:underline" style={{ color: '#292524' }}>{n.title}</span>
                          {n.published_at && <p className="text-xs mt-0.5" style={{ color: '#A8A29E' }}>{fmtMonth(n.published_at)}</p>}
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Other mills in this district, by distance */}
              <div className="rounded-[14px] p-4 mb-6" style={{ background: '#FFFFFF', border: '1px solid #E7E0D3' }}>
                <NearbyMillsDistanceList millId={mill?.id} millName={mill ? localizedFactoryName(mill, language) : undefined} accentColor="#F59E0B" titleColor="#B45309" />
              </div>

              {/* Summary */}
              <div className="rounded-[14px] p-5 mb-6" style={{ background: '#E7F7EF', border: '1px solid #B7E8CE' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(180deg, #F59E0B 0%, #D97706 100%)' }}><Icon name="Zap" size={14} color="#FFFFFF" /></div>
                  <span className="text-sm font-bold" style={{ color: '#0F2238' }}>{t('mandiFactory.summaryTitle')}</span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: '#365314' }}>
                  Allocation {metrics.latest_qty_mt < metrics.max_qty_mt ? 'declined from a peak of' : 'reached a new peak of'} {fmtNum(metrics.max_qty_mt)} MT ({fmtMonth(metrics.max_qty_month)}) to {fmtNum(metrics.latest_qty_mt)} MT in {fmtMonth(metrics.latest_month)}.
                  {' '}Ranks {ranks.national_rank} of {ranks.totalMills} nationally{ranks.state_rank ? `, ${ranks.state_rank} within ${localizedStateName(mill.source_state, language)}` : ''}.
                </p>
              </div>

              {/* Compare picker */}
              <div className="rounded-[14px] p-4" style={{ background: '#EEF4FF', border: '1.5px dashed #93C5FD' }}>
                <p className="text-sm font-bold mb-2" style={{ color: '#1D4ED8' }}>Compare with another factory</p>

                <p className="text-xs font-semibold mb-1" style={{ color: '#64748B' }}>Quick search</p>
                <input value={compareSearch} onChange={(e) => setCompareSearch(e.target.value)} placeholder="Type a factory name..."
                  className="w-full text-sm rounded-lg px-2.5 py-2 mb-2" style={{ background: '#FFFFFF', border: '1px solid #BFDBFE', color: '#292524' }} />
                {compareSearchResults.length > 0 && (
                  <div className="rounded-lg mb-3 overflow-hidden" style={{ border: '1px solid #BFDBFE' }}>
                    {compareSearchResults.map((m) => (
                      <button key={m.id} onClick={() => { setCompareMill(m.canonical_name); setCompareSearch(m.canonical_name); }}
                        className="w-full text-left text-sm px-2.5 py-2 hover:bg-[#EEF4FF]" style={{ background: '#FFFFFF', color: '#292524', borderTop: '1px solid #EEF4FF' }}>
                        {localizedFactoryName(m, language)}
                      </button>
                    ))}
                  </div>
                )}

                <p className="text-xs font-semibold mb-1" style={{ color: '#64748B' }}>Or browse by location</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <select value={compareState} onChange={(e) => { setCompareState(e.target.value); setCompareDistrict(''); setCompareMill(''); }} className="text-sm rounded-lg px-2.5 py-2" style={{ background: '#FFFFFF', border: '1px solid #BFDBFE', color: '#292524' }}>
                    <option value="">State</option>
                    {compareStates.map((s) => <option key={s} value={slugify(s)}>{localizedStateName(s, language)}</option>)}
                  </select>
                  <select value={compareDistrict} onChange={(e) => { setCompareDistrict(e.target.value); setCompareMill(''); }} disabled={!compareState} className="text-sm rounded-lg px-2.5 py-2 disabled:opacity-40" style={{ background: '#FFFFFF', border: '1px solid #BFDBFE', color: '#292524' }}>
                    <option value="">District</option>
                    {compareDistrictsForState.map((d) => <option key={d} value={slugify(d)}>{localizedDistrictName(d, compareStateCanonical, language)}</option>)}
                  </select>
                </div>
                <select value={compareMill} onChange={(e) => setCompareMill(e.target.value)} disabled={!compareState} className="w-full text-sm rounded-lg px-2.5 py-2 mb-2 disabled:opacity-40" style={{ background: '#FFFFFF', border: '1px solid #BFDBFE', color: '#292524' }}>
                  <option value="">Select factory</option>
                  {compareMillsForState.map((m) => <option key={m.id} value={m.canonical_name}>{localizedFactoryName(m, language)}</option>)}
                </select>

                <button onClick={handleGoCompare} disabled={!compareMill} className="w-full text-sm font-bold rounded-lg py-2 disabled:opacity-40" style={{ background: '#3B82F6', color: '#FFFFFF' }}>{t('marketIntelligence.compareButton')}</button>
              </div>
            </>
          )}
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg z-50" style={{ background: '#0F2238' }}>
          {toast}
        </div>
      )}
    </div>
  );
};

function buildSeasonalSeries(history, seasonal) {
  const byYear = {};
  history.forEach((r) => {
    const d = new Date(r.period_month);
    const y = d.getFullYear();
    const m = d.getMonth();
    byYear[y] = byYear[y] || {};
    if (m === seasonal.monthA) byYear[y].a = Number(r.allocated_qty_mt);
    if (m === seasonal.monthB) byYear[y].b = Number(r.allocated_qty_mt);
  });
  return Object.keys(byYear).sort().map((y) => ({ year: y, a: byYear[y].a ?? null, b: byYear[y].b ?? null })).filter((r) => r.a !== null || r.b !== null);
}

export default FactoryPage;
